/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { useTranslation } from '@scuf/localization';
import React, { memo, useContext } from 'react';
import descriptors from '../../../../locales/en-US/descriptors';
import DetailSummaryComponent from '../../../shared/DetailSummaryComponent/DetailSummaryComponent';
import TrendChart from '../../../shared/TrendChart/TrendChart';
import {
  convertDataForCarbonTrendChart,
  mergeMonthAggregations
} from '../../../shared/Utils/CarbonViewDetailsUtils';
import {
  IAny,
  ICarbonAvoidanceData,
  ICarbonScopeOneData
} from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import {
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD
} from '../../../shared/Utils/Constants';
import {
  getAvoidanceTotalAggregationValues,
  getCarbonTotalAggregationValues,
  getCarbonTotalEmissionValues
} from '../../../shared/Utils/unitHandling/EmaasSiteKpiDataScaling';
import {
  getConvertedValue,
  getMaxConvertedValueFromArray
} from '../../../shared/Utils/unitHandling/unitHandling';
import { ConversionType } from '../../../shared/Utils/unitHandling/UnitModel';
import { ApiContext } from '../../../../context/ApiContext';
import { SmartPowerSubscriptionType } from '../../../shared/Enums';
import { UserContext } from '../TotalEmissionDetailWidget/TotalEmissionDetailWidget';
import { getNullCheckedAggregationSummaryData } from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';

interface ICarbonScopeOneDetailsComponentProps {
  data: ICarbonScopeOneData;
  avoidanceData?: ICarbonAvoidanceData | undefined;
  trendChartCategory: string;
  isCarbonAvoidance?: boolean;
  forSummary?: boolean;
  forGraph?: boolean;
  // forTable?: boolean;
  carbonAllSiteMonthData?: boolean;
  dataSourceType?: string;
  isSite?: boolean;
}
const CarbonScopeOneDetailsComponent = (
  props: ICarbonScopeOneDetailsComponentProps
) => {
  const d = descriptors.energyManagement;
  const e = descriptors.DetailedView;
  const [t, lng] = useTranslation();
  const requestContext = useContext(UserContext);
  const apiContext = useContext(ApiContext);

  const {
    data,
    trendChartCategory,
    isCarbonAvoidance,
    avoidanceData,
    carbonAllSiteMonthData
  } = props;
  const {
    emissionMeasurementUnit,
    totalAggregations,
    monthWiseAggregations
  } = data;

  // let maxValueUnitConversion: any;
  // let valuesArray: any;
  // let avoidanceValuesArray: any;
  // let measurementUnit: any;

  // if (props.forGraph) {
  const valuesArray = getCarbonTotalAggregationValues([data.totalAggregations]);
  const avoidanceValuesArray = getAvoidanceTotalAggregationValues(
    avoidanceData?.totalAggregations !== undefined
      ? [avoidanceData.totalAggregations]
      : []
  );
  // }
  //  measurementUnit = data.emissionMeasurementUnit;
  // if (props.forGraph) {
  let measurementUnit = data.emissionMeasurementUnit;
  if (avoidanceValuesArray && avoidanceValuesArray.length !== 0) {
    measurementUnit =
      Math.max(...valuesArray) > Math.max(...avoidanceValuesArray)
        ? data.emissionMeasurementUnit
        : avoidanceData?.emissionMeasurementUnit ??
          data.emissionMeasurementUnit;
    valuesArray.concat(avoidanceValuesArray);
  }
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.CARBON,
    measurementUnit,
    valuesArray
  );
  let changePercentage: any;
  if (props.forSummary) {
    changePercentage =
      data.totalAggregations.totalDeviationPercentage &&
      data.totalAggregations.totalDeviationPercentage.toFixed(
        KPI_PERCENTAGE_DECIMAL_PRECISION
      );
  }

  let aggregationSummaryData: IAny;
  if (props.forSummary) {
    aggregationSummaryData = {
      actualCost: getConvertedValue(
        ConversionType.CARBON,
        data.emissionMeasurementUnit,
        data.totalAggregations.totalActualEmission,
        getMaxConvertedValueFromArray(ConversionType.CARBON, measurementUnit, [
          data.totalAggregations.totalActualEmission
        ]).Unit
      ).Value,
      actualUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        measurementUnit,
        [data.totalAggregations.totalActualEmission]
      ).Unit,
      baselineCost: getConvertedValue(
        ConversionType.CARBON,
        data.emissionMeasurementUnit,
        data.totalAggregations.totalPreviousPeriodEmission,
        getMaxConvertedValueFromArray(ConversionType.CARBON, measurementUnit, [
          data.totalAggregations.totalPreviousPeriodEmission
        ]).Unit
      ).Value,
      baselineUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        measurementUnit,
        [data.totalAggregations.totalPreviousPeriodEmission]
      ).Unit,
      baselineAvoidanceCost: getConvertedValue(
        ConversionType.CARBON,
        avoidanceData?.emissionMeasurementUnit ?? '',
        avoidanceData?.totalAggregations?.totalPreviousPeriodAvoidance ?? 0,
        maxValueUnitConversion.Unit
      ).Value,
      actualAvoidanceCost: getConvertedValue(
        ConversionType.CARBON,
        avoidanceData?.emissionMeasurementUnit ?? '',
        avoidanceData?.totalAggregations?.totalActualAvoidance ?? 0,
        maxValueUnitConversion.Unit
      ).Value,
      costSavings:
        (apiContext.showChangeWhenData &&
          (isNaN(Number(data.totalAggregations.totalPreviousPeriodEmission)) ||
            isNaN(Number(data.totalAggregations.totalActualEmission)) ||
            Number(data.totalAggregations.totalPreviousPeriodEmission) === 0 ||
            Number(data.totalAggregations.totalActualEmission) === 0 ||
            Math.abs(Number(changePercentage)) > PERCENT_CHANGE_THRESHOLD)) ||
        !carbonAllSiteMonthData
          ? null
          : getConvertedValue(
              ConversionType.CARBON,
              data.emissionMeasurementUnit,
              data.totalAggregations.totalDeviation,
              getMaxConvertedValueFromArray(
                ConversionType.CARBON,
                measurementUnit,
                [data.totalAggregations.totalDeviation]
              ).Unit
            ).Value,
      changeUnit: getMaxConvertedValueFromArray(
        ConversionType.CARBON,
        measurementUnit,
        [data.totalAggregations.totalDeviation]
      ).Unit,
      avoidanceCostSavings: getConvertedValue(
        ConversionType.CARBON,
        avoidanceData?.emissionMeasurementUnit ?? '',
        avoidanceData?.totalAggregations?.totalDeviation ?? 0,
        maxValueUnitConversion.Unit
      ).Value,
      costSavingsPercentage:
        apiContext.showChangeWhenData &&
        (isNaN(Number(data.totalAggregations.totalPreviousPeriodEmission)) ||
          isNaN(Number(data.totalAggregations.totalActualEmission)) ||
          Number(data.totalAggregations.totalPreviousPeriodEmission) === 0 ||
          Number(data.totalAggregations.totalActualEmission) === 0 ||
          Math.abs(Number(changePercentage)) > PERCENT_CHANGE_THRESHOLD ||
          !carbonAllSiteMonthData)
          ? null
          : parseFloat(
              data.totalAggregations.totalDeviationPercentage.toFixed(
                KPI_PERCENTAGE_DECIMAL_PRECISION
              )
            ),
      avoidanceCostSavingsPercentage: parseFloat(
        (
          avoidanceData?.totalAggregations?.totalDeviationPercentage ?? 0
        ).toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
      )
    };
  }
  if (props.forGraph) {
    mergeMonthAggregations(
      data.monthWiseAggregations,
      avoidanceData?.monthWiseAggregations ?? []
    );
  }
  let chartData: any;
  if (props.forGraph) {
    chartData = convertDataForCarbonTrendChart(
      data.monthWiseAggregations,
      data.emissionMeasurementUnit,
      data.totalSiteCount,
      lng
    );
  }
  const legendItems = [
    {
      text: t(d.PREVIOUSEMISSIONS),
      color: '#606061',
      className: 'emaas-legend-item-bar-baseline',
      type: 'Bar'
    },
    {
      text: t(d.ACTUALEMISSIONS),
      color: '#1272B2',
      className: 'emaas-legend-item-bar-actual',
      type: 'Bar'
    }
  ];

  if (
    requestContext?.context?.smpSubscriptionType ===
    SmartPowerSubscriptionType.PREMIUM_SUBSCRIPTION
  ) {
    legendItems.push(
      {
        text: t(d.PREVIOUSAVOIDANCE),
        color: '#4E662B',
        className: 'emaas-legend-item-bar-baseline',
        type: 'Bar'
      },
      {
        text: t(d.ACTUALAVOIDANCE),
        color: '#7DB52C',
        className: 'emaas-legend-item-bar-actual',
        type: 'Bar'
      }
    );
  }

  return (
    <>
      {props.forSummary && (
        <DetailSummaryComponent
          aggregationData={aggregationSummaryData}
          measurementUnit={maxValueUnitConversion.Unit}
          detailType={t(e.SCOPEONEKEY)}
          isCarbonAvoidance={isCarbonAvoidance}
          renderedFromCarbon={true}
          fromCarbonScopeOne={true}
        />
      )}
      {props.forGraph && (
        <TrendChart
          measurementUnit={chartData.toUnit}
          chartData={chartData}
          legendItems={legendItems}
          category={trendChartCategory}
          isCarbonAvoidance={isCarbonAvoidance}
          renderedFromCarbon={true}
          showAllTooltips
          dataSourceType={props.dataSourceType}
          isSite
        />
      )}
    </>
  );
};

export default memo(CarbonScopeOneDetailsComponent);
